import {useRef, useEffect} from 'react';
import {Link} from 'react-router';

const TOKENS = {
  primary: '#c43b71',
  ink: '#1A1A2E',
  inkMuted: '#6B7280',
  surfaceLow: 'rgba(26,26,46,0.03)',
  ghostBorder: 'rgba(26,26,46,0.08)',
  shadowSoft: '0 2px 8px rgba(0,0,0,0.04)',
};

const FONT = {
  display: 'var(--font-display), Outfit, system-ui',
  body: 'var(--font-body), Manrope, system-ui',
};

const pinkGradient = (angle) => `linear-gradient(${angle}deg, #c43b71, #d6477e)`;

export function PrimaryButton({children, onClick, disabled, style}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        height: 44,
        borderRadius: 999,
        border: 'none',
        background: pinkGradient(135),
        color: '#fff',
        fontFamily: FONT.display,
        fontSize: 15,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: '0 6px 16px rgba(196,59,113,0.28)',
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({children, onClick, style}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        height: 44,
        borderRadius: 999,
        border: 'none',
        background: 'rgba(26,26,46,0.05)',
        color: TOKENS.ink,
        fontFamily: FONT.display,
        fontSize: 15,
        fontWeight: 700,
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function FilterBar({range, tabs, action, style}) {
  return (
    <div
      className="ft-filterbar"
      role="group"
      aria-label="Filters"
      style={{display: 'flex', gap: 8, flexWrap: 'wrap', ...style}}
    >
      {range && (
        <div className="ft-filterbar-range" style={{flex: '1 1 200px'}}>
          {range}
        </div>
      )}
      {tabs && (
        <div className="ft-filterbar-tabs" style={{flex: '1 1 200px'}}>
          {tabs}
        </div>
      )}
      {action && <div className="ft-filterbar-action">{action}</div>}
    </div>
  );
}

export function AddButton({label, href, onClick}) {
  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 44,
    padding: '0 18px 0 14px',
    borderRadius: 999,
    border: 'none',
    whiteSpace: 'nowrap',
    background: pinkGradient(135),
    color: '#fff',
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: FONT.display,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    boxShadow: '0 6px 16px rgba(196, 59, 113, 0.28)',
  };
  const inner = (
    <>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
      </svg>
      {label}
    </>
  );
  if (href)
    return (
      <Link to={href} style={style}>
        {inner}
      </Link>
    );
  return (
    <button type="button" onClick={onClick} style={style}>
      {inner}
    </button>
  );
}

export function SegmentedControl({label, options, value, onChange, fullWidth = true, style}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        display: fullWidth ? 'flex' : 'inline-flex',
        width: fullWidth ? '100%' : undefined,
        padding: 4,
        borderRadius: 999,
        gap: 2,
        background: 'rgba(196, 59, 113, 0.08)',
        ...style,
      }}
    >
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(o.value)}
            style={{
              flex: fullWidth ? 1 : undefined,
              minWidth: 0,
              height: 36,
              padding: '0 14px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: checked ? 'rgba(255,255,255,0.94)' : 'transparent',
              boxShadow: checked ? TOKENS.shadowSoft : 'none',
              color: checked ? TOKENS.primary : TOKENS.ink,
              fontFamily: FONT.body,
              fontSize: 13.5,
              fontWeight: checked ? 700 : 600,
              transition: 'all 160ms ease',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function EmptyState({icon, title, body, action, primary = false, style}) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 8,
        padding: '36px 20px',
        maxWidth: 360,
        margin: '0 auto',
        ...style,
      }}
    >
      {icon && (
        <span
          aria-hidden="true"
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            marginBottom: 6,
            background: 'rgba(255,255,255,0.85)',
            boxShadow: TOKENS.shadowSoft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </span>
      )}
      <p style={{fontFamily: FONT.display, fontSize: 17, fontWeight: 800, color: TOKENS.ink, margin: 0}}>
        {title}
      </p>
      {body && (
        <p style={{fontFamily: FONT.body, fontSize: 14, lineHeight: 1.5, color: TOKENS.inkMuted, margin: 0}}>
          {body}
        </p>
      )}
      {action &&
        (primary ? (
          <div style={{width: '100%', marginTop: 12}}>
            {action.href ? (
              <AddButton label={action.label} href={action.href} />
            ) : (
              <PrimaryButton onClick={action.onClick}>{action.label}</PrimaryButton>
            )}
          </div>
        ) : (
          <div style={{marginTop: 4}}>
            <button
              onClick={action.onClick}
              style={{
                background: 'none',
                border: 'none',
                color: TOKENS.primary,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {action.label}
            </button>
          </div>
        ))}
    </div>
  );
}

export function LoadingRows({rows = 3, height = 64}) {
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
      {Array.from({length: rows}).map((_, i) => (
        <div
          key={i}
          style={{
            height,
            borderRadius: 16,
            background: 'rgba(26,26,46,0.04)',
            animation: 'pulse 1.5s infinite',
          }}
        />
      ))}
    </div>
  );
}

export function ListCard({children, style}) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.85)',
        borderRadius: 16,
        border: '1px solid #E5E7EB',
        boxShadow: TOKENS.shadowSoft,
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function ListCardSummary({expanded, onToggle, children}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      style={{
        width: '100%',
        minHeight: 44,
        padding: '12px 16px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        textAlign: 'left',
      }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        style={{
          transform: expanded ? 'rotate(90deg)' : 'none',
          transition: 'transform 160ms ease',
          flexShrink: 0,
        }}
      >
        <path d="M9 6l6 6-6 6" stroke={TOKENS.primary} strokeWidth="2" strokeLinecap="round" />
      </svg>
      {children}
    </button>
  );
}

export function ListCardBody({children}) {
  return <div style={{borderTop: `1px solid ${TOKENS.ghostBorder}`}}>{children}</div>;
}

export function ListCardSection({children}) {
  return (
    <div style={{padding: '12px 16px', borderBottom: `1px solid ${TOKENS.ghostBorder}`}}>{children}</div>
  );
}

export function ListCardActions({children}) {
  return <div style={{display: 'flex', gap: 8, padding: '12px 16px'}}>{children}</div>;
}

export function ActionButton({label, onClick, icon}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        border: 'none',
        background: 'rgba(196,59,113,0.05)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </button>
  );
}

export function StatusPill({children}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        padding: '2px 8px',
        borderRadius: 999,
        background: 'rgba(26,26,46,0.08)',
        color: TOKENS.ink,
        fontFamily: FONT.display,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {children}
    </span>
  );
}

export function ConfirmSheet({open, onClose, title, body, confirmLabel, onConfirm, destructive}) {
  const ref = useRef(null);

  useEffect(() => {
    if (open && ref.current && !ref.current.open) ref.current.showModal();
    else if (!open && ref.current?.open) ref.current.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="confirm-sheet-title"
      onClose={onClose}
      style={{
        padding: 0,
        border: 'none',
        borderRadius: 24,
        width: '90%',
        maxWidth: 400,
        background: '#fff',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        backdropFilter: 'blur(20px)',
      }}
    >
      <div style={{padding: 24}}>
        <h2 id="confirm-sheet-title" style={{fontFamily: FONT.display, fontSize: 20, margin: '0 0 10px'}}>
          {title}
        </h2>
        <p style={{fontFamily: FONT.body, color: TOKENS.inkMuted, marginBottom: 24}}>{body}</p>
        <div style={{display: 'flex', gap: 12}}>
          <div style={{flex: 1}}>
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          </div>
          <div style={{flex: 1}}>
            <PrimaryButton onClick={onConfirm} style={destructive ? {background: '#e11d48'} : {}}>
              {confirmLabel}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </dialog>
  );
}
